import { Link } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { faqs } from "@/mocks/faqs";

const groups = [
  { id: "collect", title: "收录与更新", desc: "什么会被收录、多久更新一次。", indexes: [0, 3] },
  { id: "source", title: "来源与核实", desc: "来源怎么标注、待核实意味着什么。", indexes: [1, 2] },
  { id: "join", title: "参与与纠错", desc: "怎么提交线索、怎么要求更正。", indexes: [4, 5] },
];

export default function Faq() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "/" },
        { "@type": "ListItem", position: 2, name: "吃瓜指南", item: "/faq" },
      ],
    },
  ];

  return (
    <>
      <Seo
        title="吃瓜指南与常见问题 · 131吃瓜证据合集"
        description="131吃瓜的收录标准、来源标注方式、待核实的含义与更新节奏都集中在这里说明，也包括如何提交线索与要求更正素材的具体步骤。"
        keywords="131吃瓜,常见问题,证据合集,来源标注,待核实"
        path="/faq"
        type="website"
        jsonLd={jsonLd}
      />
      <SiteHeader />
      <main>
        <section className="w-full bg-background-100/40 pt-24 md:pt-32 pb-10 md:pb-14 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "吃瓜指南" }]} />
            <h1 className="mt-5 font-heading text-3xl md:text-5xl font-black text-foreground-950 leading-tight">
              吃瓜指南与常见问题
            </h1>
            <p className="mt-4 max-w-2xl text-sm md:text-base leading-7 text-foreground-500">
              先弄清楚材料是怎么来的、什么样的内容会被标成待核实，再去看证据合集，判断会稳很多。
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50 py-10 md:py-16">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-8 lg:gap-14">
            <nav aria-label="问题分类导航" className="lg:sticky lg:top-28 self-start">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <p className="text-xs tracking-widest uppercase text-primary-500">Contents</p>
                <ul className="mt-4 space-y-3">
                  {groups.map((group) => (
                    <li key={group.id}>
                      <a href={`#${group.id}`} className="text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                        {group.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-5 rounded-lg border border-primary-300/40 bg-primary-100/50 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">还有别的问题？</h2>
                <p className="mt-2 text-sm leading-6 text-foreground-600">可以直接在关于我们页面留言，我们会补充进这份指南。</p>
                <Link
                  to="/about#submit"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  去留言
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </nav>

            <div className="space-y-12">
              {groups.map((group) => (
                <section key={group.id} id={group.id} className="scroll-mt-28">
                  <div className="border-b border-background-200/70 pb-4">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{group.title}</h2>
                    <p className="mt-2 text-sm text-foreground-500">{group.desc}</p>
                  </div>
                  <div className="mt-6 space-y-5">
                    {group.indexes.map((index) => {
                      const item = faqs[index];
                      if (!item) return null;
                      return (
                        <article
                          key={item.q}
                          itemScope
                          itemType="https://schema.org/Question"
                          className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6"
                        >
                          <h3
                            itemProp="name"
                            className="font-heading text-base md:text-lg font-bold text-foreground-950"
                          >
                            {item.q}
                          </h3>
                          <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer" className="mt-3">
                            <p itemProp="text" className="text-sm md:text-base leading-7 text-foreground-500">
                              {item.a}
                            </p>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}